import { DayLabel, TableActions, TableContainer } from '@shared/components'
import { useLocales } from '@shared/locales'
import { Flex, TableColumnsType, Tag } from 'antd'
import { MenuProps } from 'antd/lib'
import { Edit2, Eye, Trash } from 'iconsax-react'
import { useMemo, useState } from 'react'

import { ContractsHeader } from './ContractsHeader'
import { DeleteContract } from './DeleteContract'
import { EditContract } from './EditContract'
import { GenerateContract } from './GenerateContract'
import { mockContracts } from './mockData'
import { PreviewContract } from './PreviewContract'
import { ActionsType, Contract } from './types'

type ModalType = 'generate' | 'edit' | 'delete' | 'preview'

const modalTypes: Record<ModalType, React.ComponentType<ActionsType>> = {
  generate: GenerateContract,
  edit: EditContract,
  delete: DeleteContract,
  preview: PreviewContract
}

const statusColors: Record<Contract['status'], string> = {
  draft: 'default',
  pending: 'orange',
  signed: 'green',
  expired: 'red',
  cancelled: 'red'
}

export const ContractsColTrendsetter = () => {
  const { t } = useLocales()
  const [contracts, setContracts] = useState<Contract[]>(mockContracts)
  const [modalState, setModalState] = useState<{
    type: ModalType | null
    contract: Contract | null
  }>({ contract: null, type: null })

  const actionItems = (contract: Contract): MenuProps['items'] => [
    {
      label: t('generic.preview'),
      key: 'preview',
      icon: <Eye size={20} />,
      onClick: () => setModalState({ contract, type: 'preview' })
    },
    {
      type: 'divider'
    },
    {
      label: t('buttons.edit'),
      key: 'edit',
      icon: <Edit2 size={20} />,
      onClick: () => setModalState({ contract, type: 'edit' })
    },
    {
      type: 'divider'
    },
    {
      label: t('buttons.delete'),
      key: 'delete',
      danger: true,
      icon: <Trash size={20} />,
      onClick: () => setModalState({ contract, type: 'delete' })
    }
  ]

  const handleGenerateSuccess = () => {
    // Demo: Add a new contract to the list
    const now = new Date()
    const day = String(now.getDate()).padStart(2, '0')
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const year = now.getFullYear()
    const contractNumber = contracts.length + 1
    const contractNumberStr = `CONTRACT № ${contractNumber}/TR-AB-LED/${year} din ${day}/${month}/${year}`

    const newContract: Contract = {
      id: contracts.length + 1,
      contract_number: contractNumberStr,
      title: contractNumberStr,
      status: 'draft',
      created_at: now.toISOString(),
      pdf_url:
        'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf'
    }
    setContracts([...contracts, newContract])
  }

  const handleEditSuccess = () => {
    // In real implementation, would update the contract in the list
    // For demo, we just refresh the state
    setContracts([...contracts])
  }

  const handleDeleteSuccess = () => {
    if (modalState.contract) {
      setContracts(contracts.filter((c) => c.id !== modalState.contract!.id))
    }
  }

  const columns: TableColumnsType<Contract> = [
    {
      title: t('generic.name') || 'Name',
      dataIndex: 'title',
      key: 'title'
    },
    {
      title: t('contracts.status') || 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: Contract['status']) => (
        <Tag color={statusColors[status]}>
          {t(`contracts.status_${status}`) || status}
        </Tag>
      )
    },
    {
      title: t('contracts.signed_at') || 'Signed At',
      dataIndex: 'signed_at',
      key: 'signed_at',
      render: (signed_at) => (signed_at ? <DayLabel date={signed_at} /> : '-')
    },
    {
      title: t('contracts.deadline') || 'Deadline',
      dataIndex: 'deadline_at',
      key: 'deadline_at',
      render: (deadline_at) =>
        deadline_at ? <DayLabel date={deadline_at} /> : '-'
    },
    {
      title: t('filter.created_at') || 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at) => <DayLabel date={created_at} />
    },
    {
      width: 50,
      key: 'actions',
      fixed: 'right',
      render: (_, contract) => <TableActions options={actionItems(contract)} />
    }
  ]

  const actionComponent = useMemo(() => {
    if (!modalState.type) return null

    const Component = modalTypes[modalState.type]

    const handleSuccess = () => {
      switch (modalState.type) {
        case 'generate':
          handleGenerateSuccess()
          break
        case 'edit':
          handleEditSuccess()
          break
        case 'delete':
          handleDeleteSuccess()
          break
      }
    }

    return (
      <Component
        open
        contract={modalState.contract ?? undefined}
        onCancel={() => setModalState({ type: null, contract: null })}
        onSuccess={handleSuccess}
      />
    )
  }, [modalState.type, modalState.contract])

  const handleRowClick = (record: Contract) => {
    setModalState({ contract: record, type: 'preview' })
  }

  return (
    <Flex vertical gap={16}>
      <TableContainer
        rowKey="id"
        columns={columns}
        dataSource={contracts}
        loading={false}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' }
        })}
        rightChildren={
          <ContractsHeader
            onGenerate={() =>
              setModalState({ type: 'generate', contract: null })
            }
          />
        }
      />

      {actionComponent}
    </Flex>
  )
}
